import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, Sparkles, TrendingUp, AlertCircle, Lightbulb } from "lucide-react";
import { useState } from "react";

interface AIFeedback {
  strengths: string;
  weaknesses: string;
  suggestions: string;
}

interface AIFeedbackCardProps {
  feedback: AIFeedback;
  score?: number;
}

export const AIFeedbackCard = ({ feedback, score }: AIFeedbackCardProps) => {
  const [strengthsOpen, setStrengthsOpen] = useState(true);
  const [weaknessesOpen, setWeaknessesOpen] = useState(true);
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-orange-600";
  };

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle className="text-xl">AI Mentor Feedback</CardTitle>
          </div>
          {score !== undefined && (
            <Badge variant="outline" className={`text-lg font-bold ${getScoreColor(score)}`}>
              {score}/100
            </Badge>
          )}
        </div>
        <CardDescription>
          AI-powered analysis of your startup idea
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Strengths */}
        <Collapsible open={strengthsOpen} onOpenChange={setStrengthsOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-green-50 dark:bg-green-950/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-950/30 transition-colors">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-green-900 dark:text-green-100">Strengths</h3>
            </div>
            {strengthsOpen ? (
              <ChevronUp className="h-4 w-4 text-green-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-green-600" />
            )}
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3 px-4">
            <p className="text-sm text-muted-foreground">{feedback.strengths}</p>
          </CollapsibleContent>
        </Collapsible>

        {/* Weaknesses */}
        <Collapsible open={weaknessesOpen} onOpenChange={setWeaknessesOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-950/30 transition-colors">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <h3 className="font-semibold text-orange-900 dark:text-orange-100">Areas to Improve</h3>
            </div>
            {weaknessesOpen ? (
              <ChevronUp className="h-4 w-4 text-orange-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-orange-600" />
            )}
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3 px-4">
            <p className="text-sm text-muted-foreground">{feedback.weaknesses}</p>
          </CollapsibleContent>
        </Collapsible>

        {/* Suggestions */}
        <Collapsible open={suggestionsOpen} onOpenChange={setSuggestionsOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-950/30 transition-colors">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-blue-600" />
              <h3 className="font-semibold text-blue-900 dark:text-blue-100">Actionable Suggestions</h3>
            </div>
            {suggestionsOpen ? (
              <ChevronUp className="h-4 w-4 text-blue-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-blue-600" />
            )}
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3 px-4">
            <p className="text-sm text-muted-foreground">{feedback.suggestions}</p>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};
